// DATA
import { memo } from "react";
import { type DeepReadonly } from "@/utils/tsmagic";
import { AbsoluteFill, Sequence } from "remotion";

import { type WrapVideo } from "@matan/database";

import { AudioCardObjectComponent } from "./cardObjects/AudioCardObject";
import { CustomCardObjectComponent } from "./cardObjects/CustomCardObject";
import { ImageCardObjectComponent } from "./cardObjects/ImageCardObject";
import { RawTextCardObjectComponent } from "./cardObjects/RawTextCardObject";
import { RichTextCardObjectComponent } from "./cardObjects/RichTextCardObject";
import { VideoCardObjectComponent } from "./cardObjects/VideoCardObject";
import { AssetManagerProvider } from "./core/AssetManager";
import { BufferingCatOverlay } from "./core/BufferingCatOverlay";
import {
  BoundsDebugContext,
  type EditorSurface,
} from "./core/boundsDebug";
import { type ReadOnlyCardObjects } from "./core/derivedSchema";
import { HoldPlaybackUntilAssetsLoaded } from "./core/HoldPlaybackUntilAssetsLoaded";
import { PreloadLaterImages } from "./core/PreloadLaterImages";
import Endplate from "./Endplate";

export type RootCompositionProps = {
  cardId: string | undefined;
  withEndplate?: boolean;
  baseFont?: string;
  data: {
    cardObjects: ReadOnlyCardObjects;
    wrapVideo: DeepReadonly<WrapVideo>;
  } | null;
  background?: string;
  /**
   * Which editor surface is rendering, which decides WHICH text objects get an
   * outline and in what colour: an artist sees every box in its bounds colour
   * and consumer-editable text in green, a consumer sees only their own
   * editable field. Defaults to "off" so `/view` and the Lambda MP4 never show
   * any of it. See core/boundsDebug + LLD §6.6.
   */
  boundsSurface?: EditorSurface;
  /**
   * Draw the purring cat while the Player is buffering (#791). `/view` and
   * `/preview` only; never drawn into a render. See BufferingCatOverlay.
   */
  loadingCat?: boolean;
};

export const RootComposition = ({
  data,
  withEndplate,
  baseFont,
  background = "white",
  boundsSurface = "off",
  loadingCat,
}: RootCompositionProps) => {
  if (data === null) {
    throw new Error("Data was not fetched");
  }
  return (
    <BoundsDebugContext.Provider value={boundsSurface}>
    <AssetManagerProvider cardObjects={data.cardObjects}>
      {/* Autoplay waits for the asset URLs; see HoldPlaybackUntilAssetsLoaded. */}
      <HoldPlaybackUntilAssetsLoaded />
      {/* Mid-card images don't hold playback; fetch them early instead. */}
      <PreloadLaterImages cardObjects={data.cardObjects} />
      <AbsoluteFill
        style={{
          background: background,
          fontFamily: baseFont,
          zIndex: 0,
        }}
      >
        <Sequence
          durationInFrames={data.wrapVideo.durationInFrames}
          layout="none"
        >
          <RootCompositionInner cardObjects={data.cardObjects} />
        </Sequence>
      </AbsoluteFill>

      {withEndplate ? (
        <Sequence
          from={data.wrapVideo.durationInFrames + 1}
          durationInFrames={60}
          layout="none"
        >
          <Endplate wrapVideo={data.wrapVideo} />
        </Sequence>
      ) : null}

      {loadingCat ? <BufferingCatOverlay /> : null}
    </AssetManagerProvider>
    </BoundsDebugContext.Provider>
  );
};

export const RootCompositionInner: React.FC<{
  cardObjects: ReadOnlyCardObjects;
}> = ({ cardObjects }) => {
  return cardObjects.map((item) => <CardObjectView key={item.id} item={item} />);
};

/**
 * One card object, memoized on the object's identity (#806, show stopper).
 *
 * Every slider tick in the editors hands the Player a new cardObjects snapshot,
 * which re-renders the whole composition. valtio keeps the identity of
 * unchanged objects between snapshots, so `memo` lets a background <Video>
 * skip the redraw while a text object rotates; before, a large video
 * re-rendered on every tick and the editor became unusable. Frame-driven
 * updates still flow through Remotion's contexts, which `memo` doesn't block.
 */
const CardObjectView = memo(function CardObjectView({
  item,
}: {
  item: ReadOnlyCardObjects[number];
}) {
  switch (item.type) {
    case "RAWTEXT":
      return <RawTextCardObjectComponent cardObject={item} />;
    case "RICHTEXT":
      return <RichTextCardObjectComponent cardObject={item} />;
    case "IMAGE":
      return <ImageCardObjectComponent cardObject={item} />;
    case "VIDEO":
      return <VideoCardObjectComponent cardObject={item} />;
    case "AUDIO":
      return <AudioCardObjectComponent cardObject={item} />;
    case "CUSTOM":
      return <CustomCardObjectComponent cardObject={item} />;
    default:
      return null;
  }
});

export default RootComposition;
